<template>
  <iframe
    id="tour"
    ref="tourFrame"
    @load="onIframeLoad"
    :src="sceneManager.iframeSrc"
  >
  </iframe>
</template>

<script setup>
import { ref, defineProps, watch } from "vue";
import { sceneManager } from "@/store/sceneManager";

defineProps({
  msg: String,
  mediaIndex: Number,
});

const tourFrame = ref(null);
// const rp = ref(null);
const isIframeLoaded = ref(false);

function onIframeLoad() {
  if (tourFrame.value) {
    let iframe = tourFrame.value;
    let iframeWindow = iframe.contentWindow;
    sceneManager.iframeWindow = iframeWindow;
    
    // Update state_0 visibility after iframe loads
    setTimeout(updateState0Visibility, 100);
  }
}

// Expose a global function for nested iframes to hide tags
// This allows panel iframes to call window.parent.parent.hideTagFromPanel()
window.hideTagFromPanel = function(tag) {
  console.log(`[tour.vue] hideTagFromPanel called from nested iframe with tag: ${tag}`);
  
  if (tourFrame.value && tourFrame.value.contentWindow) {
    try {
      const iframeWindow = tourFrame.value.contentWindow;
      
      // Check if tour object exists and is loaded
      if (iframeWindow.tour && typeof iframeWindow.tour._getRootPlayer === 'function') {
        const rp = iframeWindow.tour._getRootPlayer();
        
        if (rp && rp.setOverlaysVisibilityByTags) {
          // Use setTimeout for 1 second delay instead of 3dvista's delay parameter
          setTimeout(() => {
            rp.setOverlaysVisibilityByTags(
              [tag],
              false, // hide the tag
              rp.getMediaByName(),
              "or",
              0 // no 3dvista delay, using setTimeout instead
            );
            console.log(`[tour.vue] Successfully hidden tag: ${tag} after 1 second delay`);
          }, 600);
        } else {
          console.error('[tour.vue] Root player or setOverlaysVisibilityByTags not available');
        }
      } else {
        console.error('[tour.vue] Tour object not ready yet');
      }
    } catch (error) {
      console.error('[tour.vue] Error hiding tag:', error);
    }
  } else {
    console.error('[tour.vue] Tour iframe not available');
  }
};

// Function to hide/show state_0 tag based on active category
function updateState0Visibility(retryCount = 0) {
  const maxRetries = 10; // Prevent infinite retries
  
  if (tourFrame.value && tourFrame.value.contentWindow) {
    try {
      const iframeWindow = tourFrame.value.contentWindow;
      
      // Check if tour object exists and is loaded
      if (iframeWindow.tour && typeof iframeWindow.tour._getRootPlayer === 'function') {
        const rp = iframeWindow.tour._getRootPlayer();
        
        if (rp && rp.setOverlaysVisibilityByTags) {
          // Hide state_0 when on Overview (Viewpoints) tab, show otherwise
          const shouldHide = sceneManager.activeCategory === "Viewpoints";
          rp.setOverlaysVisibilityByTags(
            ["state_0"],
            false, // always hide
            rp.getMediaByName(),
            "or",
            1000 // delay
          );
          console.log(`State_0 visibility updated: ${shouldHide ? 'hidden' : 'shown'} for category: ${sceneManager.activeCategory}`);
        }
      } else if (retryCount < maxRetries) {
        console.log(`Tour not ready yet, retrying in 500ms... (attempt ${retryCount + 1}/${maxRetries})`);
        // Retry after a short delay if tour is not ready
        setTimeout(() => updateState0Visibility(retryCount + 1), 500);
      } else {
        console.log("Max retries reached for state_0 visibility update");
      }
    } catch (error) {
      if (retryCount < maxRetries) {
        console.log(`Error updating state_0 visibility, retrying... (attempt ${retryCount + 1}/${maxRetries}):`, error);
        // Retry after a short delay on error
        setTimeout(() => updateState0Visibility(retryCount + 1), 500);
      } else {
        console.log("Max retries reached due to error:", error);
      }
    }
  }
}

// Watch for changes in activeCategory
watch(
  () => sceneManager.activeCategory,
  () => {
    // Add a small delay to ensure iframe is ready
    setTimeout(updateState0Visibility, 100);
  }
);

window.addEventListener("message", (event) => {
  if (event.data === "tourLoaded") {
    // rp.value = iframeWindow.tour._getRootPlayer();
    // console.log(rp.value.mainPlayList.get("selectedIndex"));
    
    // Update state_0 visibility when tour is loaded
    setTimeout(updateState0Visibility, 100);
  }
  if (event.data.toString().startsWith("VP")) {
    console.log("VP received: ", event.data);
    sceneManager.setCurrentScene(event.data);
  }
  // Sync noise scenario from tour iframe
  if (
    typeof event.data === "string" &&
    (event.data === "mitigated" || event.data === "unmitigated" || event.data === "prevailed")
  ) {
    sceneManager.setNoiseScenario(event.data);
  }
  if (event.data && event.data.type === 'noiseScenario') {
    const value = event.data.value;
    if (value === 'mitigated' || value === 'unmitigated' || value === 'prevailed') {
      sceneManager.setNoiseScenario(value);
    }
  }
  if (event.data && event.data.type === 'noiseLegendFlags') {
    sceneManager.setNoiseLegendFlags(event.data.flags || []);
  }
  // Handle hideTag messages from webframe panels
  if (event.data && event.data.type === 'hideTag') {
    const tag = event.data.tag;
    const delay = event.data.delay || 0;
    
    if (tourFrame.value && tourFrame.value.contentWindow) {
      try {
        const iframeWindow = tourFrame.value.contentWindow;
        if (iframeWindow.tour && typeof iframeWindow.tour._getRootPlayer === 'function') {
          const rp = iframeWindow.tour._getRootPlayer();
          if (rp && rp.setOverlaysVisibilityByTags) {
            rp.setOverlaysVisibilityByTags(
              [tag],
              false, // hide the tag
              rp.getMediaByName(),
              "or",
              delay
            );
            console.log(`Tag ${tag} hidden via postMessage`);
          }
        }
      } catch (error) {
        console.error("Error hiding tag:", error);
      }
    }
  }
});
</script>

<style scoped>
iframe {
  height: 100%;
  width: 100%;
  padding: 0;
  margin: 0;
  border: none;
}

button {
  /* button in the middle of screen */
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 10px 20px;
  font-size: 20px;
  cursor: pointer;
  background-color: #f0f0f0;
  border: none;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: background-color 0.3s;
}
</style>
